import { Spin } from 'antd';
import { lazy, Suspense } from 'react';
import { createBrowserRouter, RouterProvider, useRouteError } from 'react-router-dom';
import './App.scss';
import { LoginForm } from './components/Login/LoginForm';
import { RequireAuth } from './components/RequireAuth';
import { AuthProvider } from './context/AuthProvider.js';
import './ProductApp.scss';

//Everything behind the login is code-split, so the login page only loads what it needs
const named = (load, name) => lazy(() => load().then(m => ({ default: m[name] })))

const Layout = named(() => import('./Layout'), 'Layout')
const Home = named(() => import('./components/Home'), 'Home')
const Logout = named(() => import('./components/Logout'), 'Logout')
const ProductsGrid = named(() => import('./components/ProductsGrid'), 'ProductsGrid')
const UsersList = named(() => import('./components/Users/UsersList'), 'UsersList')
const AddUserForm = named(() => import('./components/Users/AddUserForm'), 'AddUserForm')
const ChangePasswordForm = named(() => import('./components/Users/ChangePasswordForm'), 'ChangePasswordForm')
const SyncRuns = named(() => import('./components/SyncRuns/SyncRuns'), 'SyncRuns')
const MappingApp = lazy(() => import('./MappingApp'))
const ProductApp = lazy(() => import('./ProductApp'))
const CategoryMargins = lazy(() => import('./components/Settings/CategoryMargins'))
const MarginFields = lazy(() => import('./components/Settings/MarginFields'))

//Route loaders/actions live on the lazy modules, so import them on demand (react-router 6.8 has no route.lazy)
const loadCategoryMargins = () => import('./components/Settings/CategoryMargins').then(m => m.default)
const loadMarginFields = () => import('./components/Settings/MarginFields').then(m => m.default)

const page = (Component) => (
  <Suspense fallback={<div style={{ padding: 48, textAlign: 'center' }}><Spin /></div>}>
    <Component />
  </Suspense>
)

function App() {

  return (
    <div className="page-content">
      <AuthProvider>
        <RouterProvider router={createBrowserRouter([
          {
            path: '/',
            errorElement: <ErrorBoundary />,
            children: [
              {
                //Outside Layout: no menu until logged in
                path: '/login',
                element: <LoginForm />,
              },
              {
                element: <RequireAuth />,
                children: [
                  {
                    element: page(Layout),
                    children: [
                      {
                        index: true,
                        element: page(Home),
                      },
                      {
                        path: '/logout',
                        element: page(Logout),
                      },
                      {
                        path: '/users',
                        element: page(UsersList),
                      },
                      {
                        path: '/users/new',
                        element: page(AddUserForm),
                      },
                      {
                        path: '/sync-runs',
                        element: page(SyncRuns),
                      },
                      {
                        path: '/account/password',
                        element: page(ChangePasswordForm),
                      },
                      {
                        path: 'supplier/:supplier',
                        children: [
                          {
                            path: ':filterStatus?',
                            element: page(ProductsGrid)
                          },
                          {
                            path: 'product/:code',
                            element: page(ProductApp),
                          },
                          {
                            path: 'mappings/:specification',
                            element: page(MappingApp),
                          },
                          {
                            path: 'margins',
                            loader: (args) => loadCategoryMargins().then(c => c.Loader(args)),
                            element: page(CategoryMargins),
                            children: [
                              {
                                id: "CategoryMarginSingle",
                                path: ':categoryId',
                                element: page(MarginFields),
                                loader: (args) => loadMarginFields().then(c => c.Loader(args)),
                                action: (args) => loadMarginFields().then(c => c.Action(args)),
                              }
                            ]
                          },
                        ]
                      },
                    ]
                  },
                ]
              }

            ]
          },
        ])} />
      </AuthProvider>
    </div >
  )
}

function ErrorBoundary() {
  let error = useRouteError();
  // if (error instanceof AuthenticationError) {
  //   return <Login onLogin={AuthenticationError.onLogin} />
  // }
  console.error(error);
  return (
    <div id="error-page">
      <h1>Oops!</h1>
      <p>Sorry, an unexpected error has occurred.</p>
      <p>
        <i>{error.statusText || error.message}</i>
      </p>
    </div>
  );
}


export default App;
